 import { SITE_CONFIG } from "@/config/site-meta";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Search, Zap, Sparkles, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import Layout from "@/components/layout/Layout";
import PortalStats from "@/components/home/PortalStats";
import HeroBanner from "@/components/home/HeroBanner";
import FeaturesSection from "@/components/home/FeaturesSection";
import HowItWorks from "@/components/home/HowItWorks";
import ThematicSections from "@/components/home/ThematicSections";
import HomeCarousels from "@/components/home/HomeCarousels";
import ScooterTypes from "@/components/home/ScooterTypes";
import PricingPlans from "@/components/home/PricingPlans";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, lazy, Suspense } from "react";
import { useSEO } from "@/hooks/useSEO";

// Lazy: dialog opens after 15s, no need in initial JS bundle.
const NewsletterPopup = lazy(() => import("@/components/newsletter/NewsletterPopup"));


const CATEGORIES = [
  { label: "Nuevos", value: "nuevo", emoji: "✨" },
  { label: "Segunda mano", value: "usado", emoji: "♻️" },
  { label: "Recambios", value: "recambios", emoji: "🔧" },
  { label: "Homologados DGT", value: "dgt", emoji: "🛡️" },
];


const Index = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [popularBrands, setPopularBrands] = useState<string[]>(["Xiaomi", "Segway", "Dualtron"]);

  useSEO({
    title: `${SITE_CONFIG.name} | Compra y Venta Segura en España`,
    description: SITE_CONFIG.description,
    canonical: "/",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": SITE_CONFIG.name,
        "alternateName": SITE_CONFIG.alternateName,
        "url": SITE_CONFIG.url,
        "inLanguage": "es-ES",
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${SITE_CONFIG.url}/buscar?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": SITE_CONFIG.name,
        "url": SITE_CONFIG.url,
        "logo": SITE_CONFIG.logo,
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": SITE_CONFIG.contactPhone,
          "contactType": "customer service",
          "email": SITE_CONFIG.contactEmail,
          "areaServed": SITE_CONFIG.areaServed,
          "availableLanguage": "Spanish"
        },
        "sameAs": Object.values(SITE_CONFIG.socials)
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": SITE_CONFIG.url
          }
        ]
      }
    ],
  });

  useEffect(() => {
    // Defer this non-critical fetch until the browser is idle so it doesn't
    // compete with above-the-fold rendering and improves TTI.
    const run = () => {
      supabase.from("site_settings").select("value").eq("key", "featured_brands").maybeSingle().then(({ data }) => {
        if (data?.value && Array.isArray(data.value) && data.value.length > 0) {
          setPopularBrands(data.value as string[]);
        }
      });
    };
    const w = window as any;
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(run, { timeout: 2000 })
      : window.setTimeout(run, 1200);
    return () => {
      if (w.cancelIdleCallback) w.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, []);

  return (
    <Layout>
      <Suspense fallback={null}>
        <NewsletterPopup />
      </Suspense>

      {/* H1 visualmente oculto para SEO (el hero usa H2 textuales por slide) */}
      <h1 className="sr-only">
        Patinetes eléctricos de segunda mano y nuevos en España — Marketplace líder
      </h1>

      {/* Hero Banner Rotativo */}
      <HeroBanner />

      {/* Search Bar — moderno con halo gradiente */}
      <section className="relative overflow-hidden border-b border-border py-10">
        <div className="pointer-events-none absolute -top-16 left-1/2 h-56 w-[36rem] -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-16 left-1/3 h-40 w-[28rem] rounded-full bg-accent/15 blur-3xl" aria-hidden />
        <div className="container relative">
          <div className="mx-auto max-w-2xl">
            <div className="group relative">
              {/* Halo gradiente animado */}
              <div
                className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-primary via-accent to-secondary opacity-60 blur-md transition-opacity duration-300 group-focus-within:opacity-100"
                aria-hidden
              />
              <div className="relative flex items-center gap-2 rounded-2xl border border-border/60 bg-card/90 p-2 shadow-xl backdrop-blur-sm">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                  <Input
                    placeholder="Buscar por marca, modelo o ciudad..."
                    className="h-11 border-0 bg-transparent pl-10 text-base shadow-none focus-visible:ring-0"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        window.location.href = `/buscar${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""}`;
                      }
                    }}
                  />
                </div>
                <Button asChild size="lg" className="h-11 gap-1.5 rounded-xl bg-gradient-to-r from-primary to-accent shadow-md shadow-primary/30 hover:shadow-lg hover:shadow-primary/40">
                  <Link to={`/buscar${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ""}`}>
                    <Search className="h-4 w-4" /> Buscar
                  </Link>
                </Button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-secondary" /> Popular:
              </span>
              {popularBrands.map((brand) => (
                <Link
                  key={brand}
                  to={`/buscar?q=${brand}`}
                  className="rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 hover:text-primary hover:shadow-sm"
                >
                  {brand}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portal Stats */}
      <PortalStats />

      {/* Tipos de patinete - Elige entre la mayor oferta */}
      <ScooterTypes />

      {/* Planes de suscripción */}
      <PricingPlans />

      {/* Carruseles de anuncios: Recientes / Mejor precio / Más vistos / DGT */}
      <HomeCarousels />

      {/* Categories — moderno con glassmorphism y gradientes */}
      <section className="relative overflow-hidden border-b border-border py-16">
        <div className="pointer-events-none absolute -top-20 right-1/4 h-64 w-64 rounded-full bg-accent/15 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-20 left-1/4 h-64 w-64 rounded-full bg-secondary/15 blur-3xl" aria-hidden />
        <div className="container relative">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
              <Sparkles className="h-3.5 w-3.5 text-accent" /> Categorías destacadas
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight md:text-4xl">
              Explora por{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                categoría
              </span>
            </h2>
          </div>
          <div className="mt-10 grid gap-5 grid-cols-2 md:grid-cols-4">
            {CATEGORIES.map(({ label, value, emoji }, idx) => (
              <Link
                key={value}
                to={value === "dgt" ? "/buscar?dgt=1" : `/buscar?condition=${value}`}
                style={{ animationDelay: `${idx * 80}ms` }}
                className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10 animate-fade-in"
              >
                {/* Línea superior gradiente en hover */}
                <div
                  className="pointer-events-none absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  aria-hidden
                />
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="relative inline-flex">
                    <div
                      className="absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br from-primary/30 to-accent/30 opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100"
                      aria-hidden
                    />
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-muted/50 to-muted/20 text-4xl ring-1 ring-border transition-transform duration-300 group-hover:scale-110 group-hover:ring-primary/30">
                      {emoji}
                    </div>
                  </div>
                  <span className="font-display text-sm font-bold">{label}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    Ver más <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features — estilo moderno con badge, gradientes y glassmorphism */}
      <FeaturesSection />


      {/* Thematic Sections: Recién publicados, Mejores precios, Más vistos */}
      <ThematicSections />

      {/* How it works — rediseño moderno con reveal animado */}
      <HowItWorks />

      {/* CTA — moderno con glassmorphism + gradientes */}
      <section className="relative overflow-hidden border-t border-border py-20">
        <div className="pointer-events-none absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-primary/20 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-accent/20 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--secondary)/0.08),transparent_70%)]" aria-hidden />

        <div className="container relative">
          <div className="group relative mx-auto max-w-3xl">
            {/* Halo gradiente animado */}
            <div
              className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-primary via-accent to-secondary opacity-50 blur-xl transition-opacity duration-500 group-hover:opacity-80"
              aria-hidden
            />
            <div className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/80 px-6 py-12 text-center shadow-2xl backdrop-blur-md md:px-12 md:py-16">
              {/* Línea superior gradiente */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" aria-hidden />

              <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary-foreground">
                <Sparkles className="h-3.5 w-3.5 text-secondary" /> Vende sin complicaciones
              </span>

              <h2 className="mt-5 font-display text-3xl font-bold tracking-tight md:text-4xl">
                ¿Tienes un patinete que{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  no usas?
                </span>
              </h2>
               <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground md:text-lg">
                 Publícalo por solo <span className="font-semibold text-foreground">9,99 €</span> y llega a miles de compradores en toda España.
               </p>

              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Button size="lg" asChild className="gap-2 rounded-xl bg-gradient-to-r from-primary to-accent shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40">
                  <Link to="/publicar">
                    <Zap className="h-4 w-4" /> Publicar anuncio
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="gap-2 rounded-xl">
                  <Link to="/buscar">
                    <Search className="h-4 w-4" /> Explorar primero
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
